import React from 'react';
import { useStyletron } from 'baseui';
import { Checkbox } from 'baseui/checkbox';
import { Input } from 'baseui/input';

interface FreeAppConfigBoxProps {
	currentAdConfig: any;
	configPath: string[];
	adType: string;
	onUpdate: (path: string[], value: any) => void;
	getDeviceConfig: (adConfig: any, platform: string, subPlatform?: string) => any;
	createLocalDeviceConfig: (adConfig: any, platform: string, subPlatform: string | undefined, updates: any) => any;
}

const FreeAppConfigBox: React.FC<FreeAppConfigBoxProps> = ({
	currentAdConfig,
	configPath,
	adType,
	onUpdate,
	getDeviceConfig,
	createLocalDeviceConfig
}) => {
	const [css] = useStyletron();

	return (
		<div
			className={css({
				backgroundColor: '#ffffff',
				border: '2px solid #9b59b6',
				borderRadius: '12px',
				padding: '16px',
				boxShadow: '0 4px 12px rgba(155, 89, 182, 0.15)',
				transition: 'all 0.3s ease',
				':hover': {
					transform: 'translateY(-2px)',
					boxShadow: '0 8px 24px rgba(155, 89, 182, 0.2)'
				}
			})}>
			{/* Free App Header */}
			<div
				className={css({
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
					marginBottom: '16px',
					paddingBottom: '12px',
					borderBottom: '1px solid #f0f0f0'
				})}>
				<h5
					className={css({
						fontSize: '1.1rem',
						fontWeight: '700',
						color: '#9b59b6',
						margin: '0'
					})}>
					com.zupee.free
				</h5>
			</div>

			{/* Android and iOS subdivisions inside free app */}
			<div
				className={css({
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: '12px'
				})}>
				{/* Free App Android */}
				<div
					className={css({
						backgroundColor: '#f8f9fa',
						padding: '12px',
						borderRadius: '6px',
						border: '1px solid #e9ecef'
					})}>
					<div
						className={css({
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
							marginBottom: '12px'
						})}>
						<span
							className={css({
								fontSize: '0.9rem',
								fontWeight: '600',
								color: '#495057'
							})}>
							Android
						</span>
					</div>

					{(() => {
						const freeAndroidConfig = getDeviceConfig(currentAdConfig, 'com.zupee.free', 'android');
						const isUsingGlobal = !currentAdConfig.deviceConfig;
						const enabled = freeAndroidConfig?.enabled ?? false;
						const minAppVersion = freeAndroidConfig?.minAppVersion ?? '';

						return (
							<>
								<div
									className={css({
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										marginBottom: '12px'
									})}>
									<span
										className={css({
											fontSize: '0.8rem',
											fontWeight: '600',
											color: '#495057'
										})}>
										Enabled
									</span>
									<Checkbox
										checked={enabled}
										onChange={(e) => {
											if (isUsingGlobal) {
												const newDeviceConfig = createLocalDeviceConfig(
													currentAdConfig,
													'com.zupee.free',
													'android',
													{
														enabled: e.target.checked
													}
												);
												onUpdate([...configPath, adType, 'deviceConfig'], newDeviceConfig);
											} else {
												onUpdate(
													[...configPath, adType, 'deviceConfig', 'com.zupee.free', 'android', 'enabled'],
													e.target.checked
												);
											}
										}}
										overrides={{
											Checkmark: { style: { borderColor: '#27ae60' } }
										}}
									/>
								</div>
								<div>
									<label
										className={css({
											fontSize: '0.8rem',
											fontWeight: '600',
											color: '#495057',
											marginBottom: '6px',
											display: 'block'
										})}>
										Min Version
									</label>
									<Input
										value={minAppVersion}
										onChange={(e) => {
											const value = e.target.value.trim();
											const numValue = value === '' ? null : parseInt(value, 10) || null;

											if (isUsingGlobal) {
												const newDeviceConfig = createLocalDeviceConfig(
													currentAdConfig,
													'com.zupee.free',
													'android',
													{
														minAppVersion: numValue
													}
												);
												onUpdate([...configPath, adType, 'deviceConfig'], newDeviceConfig);
											} else {
												onUpdate(
													[
														...configPath,
														adType,
														'deviceConfig',
														'com.zupee.free',
														'android',
														'minAppVersion'
													],
													numValue
												);
											}
										}}
										placeholder='Version'
										overrides={{
											Root: { style: { width: '100%' } },
											Input: {
												style: {
													fontSize: '0.8rem',
													padding: '8px',
													borderRadius: '4px'
												}
											}
										}}
									/>
								</div>
							</>
						);
					})()}
				</div>

				{/* Free App iOS */}
				<div
					className={css({
						backgroundColor: '#f8f9fa',
						padding: '12px',
						borderRadius: '6px',
						border: '1px solid #e9ecef'
					})}>
					<div
						className={css({
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
							marginBottom: '12px'
						})}>
						<span
							className={css({
								fontSize: '0.9rem',
								fontWeight: '600',
								color: '#495057'
							})}>
							iOS
						</span>
					</div>

					{(() => {
						const freeIosConfig = getDeviceConfig(currentAdConfig, 'com.zupee.free', 'ios');
						const isUsingGlobal = !currentAdConfig.deviceConfig;
						const enabled = freeIosConfig?.enabled ?? false;
						const minAppVersion = freeIosConfig?.minAppVersion ?? '';

						return (
							<>
								<div
									className={css({
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										marginBottom: '12px'
									})}>
									<span
										className={css({
											fontSize: '0.8rem',
											fontWeight: '600',
											color: '#495057'
										})}>
										Enabled
									</span>
									<Checkbox
										checked={enabled}
										onChange={(e) => {
											if (isUsingGlobal) {
												const newDeviceConfig = createLocalDeviceConfig(
													currentAdConfig,
													'com.zupee.free',
													'ios',
													{
														enabled: e.target.checked
													}
												);
												onUpdate([...configPath, adType, 'deviceConfig'], newDeviceConfig);
											} else {
												onUpdate(
													[...configPath, adType, 'deviceConfig', 'com.zupee.free', 'ios', 'enabled'],
													e.target.checked
												);
											}
										}}
										overrides={{
											Checkmark: { style: { borderColor: '#27ae60' } }
										}}
									/>
								</div>
								<div>
									<label
										className={css({
											fontSize: '0.8rem',
											fontWeight: '600',
											color: '#495057',
											marginBottom: '6px',
											display: 'block'
										})}>
										Min Version
									</label>
									<Input
										value={minAppVersion}
										onChange={(e) => {
											const value = e.target.value.trim();
											const numValue = value === '' ? null : parseInt(value, 10) || null;

											if (isUsingGlobal) {
												const newDeviceConfig = createLocalDeviceConfig(
													currentAdConfig,
													'com.zupee.free',
													'ios',
													{
														minAppVersion: numValue
													}
												);
												onUpdate([...configPath, adType, 'deviceConfig'], newDeviceConfig);
											} else {
												onUpdate(
													[...configPath, adType, 'deviceConfig', 'com.zupee.free', 'ios', 'minAppVersion'],
													numValue
												);
											}
										}}
										placeholder='Version'
										overrides={{
											Root: { style: { width: '100%' } },
											Input: {
												style: {
													fontSize: '0.8rem',
													padding: '8px',
													borderRadius: '4px'
												}
											}
										}}
									/>
								</div>
							</>
						);
					})()}
				</div>
			</div>
		</div>
	);
};

export default FreeAppConfigBox;
